import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  emptyProfile,
  FOCUS_AREAS,
  FREE_SLOTS,
  OBSTACLES,
  PACES,
  SITUATIONS,
  TONES,
  type Profile,
} from "@habitos/shared";
import { api } from "../lib/api";
import { useAuth, useSetUser } from "../lib/auth";

interface Props {
  /** First run (full screen, leads into the assistant) or editing from Ajustes. */
  mode: "first" | "edit";
}

type Patch = (change: Partial<Profile>) => void;

interface Step {
  title: string;
  hint?: string;
  render: (p: Profile, set: Patch) => ReactNode;
}

function Single<T extends string>(props: { options: readonly T[]; value: T | null; onChange: (v: T | null) => void }) {
  return (
    <div className="chips">
      {props.options.map((o) => (
        <button
          type="button"
          key={o}
          className={`chip ${props.value === o ? "on" : ""}`}
          aria-pressed={props.value === o}
          onClick={() => props.onChange(props.value === o ? null : o)}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

function Multi<T extends string>(props: { options: readonly T[]; value: T[]; onChange: (v: T[]) => void }) {
  const toggle = (o: T) =>
    props.onChange(props.value.includes(o) ? props.value.filter((x) => x !== o) : [...props.value, o]);
  return (
    <div className="chips">
      {props.options.map((o) => (
        <button
          type="button"
          key={o}
          className={`chip ${props.value.includes(o) ? "on" : ""}`}
          aria-pressed={props.value.includes(o)}
          onClick={() => toggle(o)}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

const STEPS: Step[] = [
  {
    title: "¿Cómo es tu día a día?",
    render: (p, set) => (
      <>
        <Single options={SITUATIONS} value={p.situation} onChange={(situation) => set({ situation })} />
        <label>
          ¿Con quién vives o a quién cuidas? <span className="muted">(opcional)</span>
          <input
            value={p.household}
            maxLength={300}
            onChange={(e) => set({ household: e.target.value })}
            placeholder="Mi esposo y dos hijos de 3 y 6 años"
          />
        </label>
      </>
    ),
  },
  {
    title: "¿A qué hora te levantas y te duermes?",
    hint: "Así nunca te propongo algo cuando estás durmiendo.",
    render: (p, set) => (
      <div className="row gap">
        <label className="grow">
          Me levanto
          <input type="time" value={p.wakeTime ?? ""} onChange={(e) => set({ wakeTime: e.target.value || null })} />
        </label>
        <label className="grow">
          Me duermo
          <input type="time" value={p.sleepTime ?? ""} onChange={(e) => set({ sleepTime: e.target.value || null })} />
        </label>
      </div>
    ),
  },
  {
    title: "¿Cuándo sueles tener un rato libre?",
    render: (p, set) => (
      <>
        <Multi options={FREE_SLOTS} value={p.freeSlots} onChange={(freeSlots) => set({ freeSlots })} />
        <label>
          ¿Qué horarios ya tienes ocupados? <span className="muted">(opcional)</span>
          <textarea
            className="textarea"
            value={p.busyTimes}
            maxLength={500}
            onChange={(e) => set({ busyTimes: e.target.value })}
            placeholder="Llevo a los niños a la escuela de 7:30 a 8:15; la comida es a las 14:00"
          />
        </label>
      </>
    ),
  },
  {
    title: "¿Qué te gustaría mejorar?",
    hint: "Elige las que quieras.",
    render: (p, set) => (
      <>
        <Multi options={FOCUS_AREAS} value={p.focusAreas} onChange={(focusAreas) => set({ focusAreas })} />
        <label>
          En tus palabras, ¿qué te gustaría lograr o cambiar?
          <textarea
            className="textarea"
            value={p.wish}
            maxLength={1000}
            onChange={(e) => set({ wish: e.target.value })}
            placeholder="Tener las mañanas más tranquilas y retomar el ejercicio"
          />
        </label>
      </>
    ),
  },
  {
    title: "¿Qué te ha hecho dejar un hábito antes?",
    hint: "No hay respuestas malas: así diseño planes que sí se sostengan.",
    render: (p, set) => <Multi options={OBSTACLES} value={p.obstacles} onChange={(obstacles) => set({ obstacles })} />,
  },
  {
    title: "¿Cómo quieres que te hable tu asistente?",
    render: (p, set) => (
      <>
        <Single options={TONES} value={p.tone} onChange={(tone) => set({ tone })} />
        <p className="small muted" style={{ margin: "8px 0 0" }}>¿A qué ritmo prefieres avanzar?</p>
        <Single options={PACES} value={p.pace} onChange={(pace) => set({ pace })} />
      </>
    ),
  },
  {
    title: "¿Algo más que deba saber?",
    hint: "Opcional: salud, temporadas difíciles, lo que te motiva…",
    render: (p, set) => (
      <textarea
        className="textarea"
        value={p.notes}
        maxLength={1000}
        onChange={(e) => set({ notes: e.target.value })}
        placeholder="Los fines de semana casi no tengo tiempo para mí"
      />
    ),
  },
];

export function Onboarding({ mode }: Props) {
  const { user } = useAuth();
  const setUser = useSetUser();
  const navigate = useNavigate();
  const client = useQueryClient();
  const stored = useQuery({ queryKey: ["profile"], queryFn: api.profile, enabled: mode === "edit" });
  const [profile, setProfile] = useState<Profile>(emptyProfile);
  // -1 is the welcome screen, shown only on the first run.
  const [step, setStep] = useState(mode === "first" ? -1 : 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (stored.data) setProfile(stored.data);
  }, [stored.data]);

  const set: Patch = (change) => setProfile((cur) => ({ ...cur, ...change }));

  async function finish(answers: Profile) {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.saveProfile(answers);
      // Ajustes shows the daily notice times, which follow the wake and sleep times.
      client.setQueryData(["profile"], answers);
      if (mode === "first") {
        setUser(updated);
        navigate("/asistente", { replace: true });
      } else {
        navigate("/ajustes");
      }
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  if (mode === "edit" && stored.isPending) return <p className="muted center">Cargando…</p>;

  if (step === -1) {
    return (
      <div className="onboarding">
        <div className="onboarding-body empty">
          <span style={{ fontSize: "3rem" }}>👋</span>
          <h1>Hola, {user.name}</h1>
          <p>
            Te haré unas preguntas rápidas para que tu asistente te conozca y te arme planes a tu medida.
            Toma unos 2 minutos.
          </p>
        </div>
        <div className="onboarding-actions">
          <button className="btn primary" onClick={() => setStep(0)}>Empezar</button>
          <button className="link" onClick={() => void finish(emptyProfile)} disabled={busy}>Ahora no</button>
          {error && <p className="error">{error}</p>}
        </div>
      </div>
    );
  }

  const current = STEPS[step]!;
  const last = step === STEPS.length - 1;

  return (
    <div className="onboarding">
      <div className="progress" role="progressbar" aria-valuenow={step + 1} aria-valuemax={STEPS.length}>
        <button className="icon-btn" aria-label="Atrás" onClick={() => (step > 0 ? setStep(step - 1) : mode === "first" ? setStep(-1) : navigate(-1))}>‹</button>
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
        </div>
        <span>{step + 1} de {STEPS.length}</span>
      </div>
      <div className="onboarding-body form">
        <h1>{current.title}</h1>
        {current.hint && <p className="muted" style={{ margin: 0 }}>{current.hint}</p>}
        {current.render(profile, set)}
      </div>
      <div className="onboarding-actions">
        {error && <p className="error">{error}</p>}
        <button className="btn primary" disabled={busy} onClick={() => (last ? void finish(profile) : setStep(step + 1))}>
          {last ? (busy ? "Guardando…" : mode === "first" ? "Terminar y hablar con mi asistente" : "Guardar") : "Siguiente"}
        </button>
        {!last && <button className="link" onClick={() => setStep(step + 1)}>Saltar esta pregunta</button>}
      </div>
    </div>
  );
}
